// Выезжающая панель чата: доступна с любой страницы, сама подставляет контекст страницы.

import { Chip, IconButton, toast, when } from '@autoaff/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ArrowLeft, Check, HelpCircle, Maximize2, SquarePen, X } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useEffect } from 'react';
import { choiceParts } from '../models';
import { Mark } from '../shell/Mark';
import { type Ask, answerAgent, dropWaiting, useRefreshWaiting } from '../shell/waiting';
import type { AttachmentRef } from './attach';
import { Composer, Conversation } from './Conversation';
import { ModelPicker } from './ModelPicker';
import { chatStore, useChat } from './store';
import { useChatModel } from './useChatModel';
import { useChatThread } from './useChatThread';

export function ChatPanel() {
  const chat = useChat();
  const reduce = useReducedMotion();
  useEffect(() => {
    if (!chat.open) return;
    const onKey = (e: KeyboardEvent) => {
      // Палитра команд и просмотр вложения закрываются первыми
      if (e.key === 'Escape' && !document.querySelector('.pal, .lbox')) chatStore.close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [chat.open]);
  return (
    <AnimatePresence>
      {chat.open && (
        <>
          <motion.div
            key="scrim"
            className="scrim hide-m"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduce ? 0 : 0.18 }}
            onClick={() => chatStore.close()}
          />
          <motion.section
            key="panel"
            className="chat-panel"
            role="dialog"
            aria-label="Чат с агентом"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={reduce ? { duration: 0 } : { type: 'spring', stiffness: 420, damping: 40 }}
          >
            <PanelBody />
          </motion.section>
        </>
      )}
    </AnimatePresence>
  );
}

function PanelBody() {
  const chat = useChat();
  const t = useChatThread(chat.threadId, (id) => chatStore.setThread(id), chat.context);
  const cm = useChatModel(chat.threadId);
  const qc = useQueryClient();
  const refreshWaiting = useRefreshWaiting();
  useEffect(() => {
    const d = chatStore.takeDraft();
    if (d) void t.send(d);
  }, [t.send]);
  // Тред открыт ради ответа на вопрос агента: вопрос закреплён сверху, первое сообщение — ответ на него
  const ask = chat.ask && chat.ask.threadId === chat.threadId ? chat.ask : null;
  const pending = ask && !ask.answered ? ask : null;
  const send = async (text: string, attachments: AttachmentRef[] = []) => {
    if (pending) {
      try {
        await answerAgent(pending.key, text, attachments);
        chatStore.answered();
        dropWaiting(qc, pending.key);
        refreshWaiting();
        return true;
      } catch (e) {
        toast((e as Error).message);
        return false;
      }
    }
    await cm.flush();
    return t.send(text, attachments);
  };
  return (
    <>
      <header className="chat-head">
        <IconButton label="Закрыть чат" className="only-m" onClick={() => chatStore.close()}>
          <ArrowLeft />
        </IconButton>
        <div className="who">
          <span className="amark">
            <Mark />
          </span>
          <div>
            <b>Агент</b>
            <span>
              {t.view.working
                ? t.view.waiting
                  ? 'ждёт вашего решения'
                  : 'работает'
                : `${cm.value ? choiceParts(cm.value, cm.catalog).model : 'Claude'} · чат`}
            </span>
          </div>
        </div>
        <IconButton label="Новый чат" onClick={() => chatStore.setThread(null)}>
          <SquarePen />
        </IconButton>
        {chat.threadId && (
          <Link
            to="/agent/chats/$threadId"
            params={{ threadId: chat.threadId }}
            className="icon-btn hide-m"
            aria-label="Открыть на всю страницу"
            title="Открыть на всю страницу"
            onClick={() => chatStore.close()}
          >
            <Maximize2 />
          </Link>
        )}
        <IconButton label="Закрыть чат" className="hide-m" onClick={() => chatStore.close()}>
          <X />
        </IconButton>
      </header>
      {ask ? (
        <AskPlate ask={ask} />
      ) : (
        chat.context && (
          <div className="chat-ctx">
            <span>Контекст:</span>
            <Chip tone="accent">{chat.context.label}</Chip>
          </div>
        )
      )}
      <Conversation
        view={t.view}
        ready={t.ready || !chat.threadId}
        empty={<ChatWelcome onPick={(q) => void send(q)} />}
      />
      <Composer
        working={t.view.working}
        onSend={send}
        onStop={t.stop}
        onFocusInput={() => void t.ensure().catch(() => {})}
        tools={<ChatModelPicker cm={cm} />}
        placeholder={pending ? 'Ваш ответ агенту…' : undefined}
        autoFocus
      />
    </>
  );
}

/**
 * Закреплённый вопрос: что агент спросил и откуда — сверху разговора, пока владелец отвечает. Длинная лента фоновой
 * работы ниже свёрнута (задание — строкой, шаги — счётчиком), так что понятно, что здесь нужно сделать.
 */
function AskPlate({ ask }: { ask: Ask }) {
  if (ask.answered)
    return (
      <div className="chat-ask done" role="status">
        <Check aria-hidden="true" />
        <span>
          {ask.task ? 'Агент получил ответ и продолжит задачу.' : 'Агент получил ответ и продолжит.'}
        </span>
      </div>
    );
  return (
    <section className="chat-ask" aria-label="Вопрос агента">
      <HelpCircle aria-hidden="true" />
      <div>
        <p className="chat-ask-from">
          Агент спросил · {ask.source} · {when(ask.at)}
        </p>
        {ask.questions.length > 1 ? (
          <ul>
            {ask.questions.map((q) => (
              <li key={q}>{q}</li>
            ))}
          </ul>
        ) : (
          <p className="chat-ask-q">{ask.questions[0]}</p>
        )}
      </div>
    </section>
  );
}

/** Кнопка модели в поле ввода чата. */
export function ChatModelPicker({ cm }: { cm: ReturnType<typeof useChatModel> }) {
  if (!cm.value) return null;
  return (
    <ModelPicker
      value={cm.value}
      catalog={cm.catalog}
      label="Модель агента в этом чате"
      onChange={(c, effortOnly) => {
        if (c) cm.change(c, { debounce: effortOnly });
      }}
    />
  );
}

export function ChatWelcome({ onPick }: { onPick: (q: string) => void }) {
  const ideas = [
    'Что ты умеешь?',
    'Составь план, как ты будешь вести мою рекламу',
    'Какие данные тебе понадобятся от меня?',
  ];
  return (
    <div className="empty" style={{ paddingTop: 32 }}>
      <div className="empty-icon">
        <Mark />
      </div>
      <h2>Спросите агента</h2>
      <p>
        Он видит, с какой страницы вы пишете. Действия, которые что-то меняют, агент сначала попросит
        одобрить.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%', maxWidth: 360 }}>
        {ideas.map((q) => (
          <button key={q} type="button" className="btn chat-idea" onClick={() => onPick(q)}>
            {q}
          </button>
        ))}
      </div>
    </div>
  );
}
